<template>
  <div class="reset-password">
    <div class="reset-password__card">
      <div class="reset-password__header">
        <img
          src="../../assets/logo.png"
          alt="Logo"
          class="reset-password__logo"
        />
        <h1 class="reset-password__title">Ustaw nowe hasło</h1>
      </div>

      <div
        v-if="error"
        class="reset-password__error"
      >
        <div class="reset-password__icon reset-password__icon--error">
          <svg
            class="reset-password__svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 class="reset-password__subtitle">Wystąpił błąd</h2>
        <p class="reset-password__message">{{ error }}</p>
        <router-link
          to="/request-password-reset"
          class="reset-password__button"
        >
          Spróbuj ponownie
        </router-link>
      </div>

      <form
        v-else-if="!isSuccess"
        class="reset-password__form"
        @submit="onSubmit"
      >
        <div class="reset-password__form-group">
          <label
            for="password"
            class="reset-password__label"
          >
            Nowe hasło
          </label>
          <div class="reset-password__input-wrapper">
            <input
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="reset-password__input"
              :class="{ 'reset-password__input--error': passwordError }"
              placeholder="Wprowadź nowe hasło"
            />
            <button
              type="button"
              class="reset-password__toggle-password"
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
            </button>
          </div>
          <div
            v-if="passwordError"
            class="reset-password__message"
          >
            <small class="reset-password__error">{{ passwordError }}</small>
          </div>
        </div>

        <div class="reset-password__form-group">
          <label
            for="confirmPassword"
            class="reset-password__label"
          >
            Potwierdź hasło
          </label>
          <div class="reset-password__input-wrapper">
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="reset-password__input"
              :class="{ 'reset-password__input--error': confirmPasswordError }"
              placeholder="Potwierdź nowe hasło"
            />
            <button
              type="button"
              class="reset-password__toggle-password"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <i :class="showConfirmPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
            </button>
          </div>
          <div
            v-if="confirmPasswordError"
            class="reset-password__message"
          >
            <small class="reset-password__error">{{ confirmPasswordError }}</small>
          </div>
        </div>

        <button
          type="submit"
          class="reset-password__button"
          :disabled="isLoading"
        >
          <span
            v-if="isLoading"
            class="reset-password__spinner"
          ></span>
          <span v-else>Zresetuj hasło</span>
        </button>
      </form>

      <div
        v-else
        class="reset-password__success"
      >
        <div class="reset-password__icon reset-password__icon--success">
          <svg
            class="reset-password__svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 class="reset-password__subtitle">Hasło zmienione</h2>
        <p class="reset-password__message">
          Twoje hasło zostało pomyślnie zmienione. Możesz teraz zalogować się używając nowego hasła.
        </p>
        <router-link
          to="/login"
          class="reset-password__button"
        >
          Przejdź do logowania
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { UserService } from '@/api/user/userService';
import { useField, useForm } from 'vee-validate';
import { validateSchema } from '@/pages/Schemas/Schemas';
import { ResetPasswordSchema } from '@/pages/Schemas/ResetPasswordZodValidationSchema';
import { useUserStore } from '../../stores/userStore/userStore';
import { useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';

const route = useRoute();
const token = ref<string | null>(null);
const error = ref<string | null>(null);
const isLoading = ref(false);
const isSuccess = ref(false);
const buttonState = ref<'pending' | 'success' | 'error'>('pending');
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const userStore = useUserStore();
const userTokensStore = useUserTokensStore();

const { handleSubmit: handleFormSubmit } = useForm({
  validationSchema: validateSchema(ResetPasswordSchema),
  initialValues: {
    password: '',
    confirmPassword: '',
  },
});

const { value: password, errorMessage: passwordError } = useField('password');
const { value: confirmPassword, errorMessage: confirmPasswordError } = useField('confirmPassword');

onMounted(() => {
  const tokenParam = route.query['token'] as string;
  if (!tokenParam) {
    error.value = 'Nie znaleziono tokenu resetowania hasła. Sprawdź link w swojej wiadomości email.';
    return;
  }
  token.value = tokenParam;
});

const onSubmit = handleFormSubmit(async (values) => {
  if (!token.value) {
    error.value = 'Brak tokenu resetowania hasła. Proszę sprawdzić link w wiadomości email.';
    return;
  }

  try {
    error.value = null;
    isLoading.value = true;
    buttonState.value = 'pending';

    await UserService.resetPassword(token.value, values.password);

    userStore.removeUser();
    userTokensStore.removeUserTokens();

    buttonState.value = 'success';
    isSuccess.value = true;
  } catch (err) {
    buttonState.value = 'error';
    error.value = err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie później.';
  } finally {
    isLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
@use 'sass:color';

.reset-password {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $white-100 0%, $brown-200 100%);
  padding: 20px;

  &__card {
    display: flex;
    flex-direction: column;
    gap: 4rem;
    width: 100%;
    max-width: 450px;
    padding: 4rem 2.5rem;
    background: $white;
    border-radius: 1rem;
  }

  &__header {
    text-align: center;
  }

  &__logo {
    width: 180px;
    height: auto;
    margin-bottom: 1.5rem;
  }

  &__title {
    font-size: clamp(3.6rem, 4vw, 4.8rem);
    font-weight: 700;
    color: $black-400;
    margin: 0;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  &__form-group {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    position: relative;
  }

  &__label {
    font-size: 2rem;
    font-weight: 700;
    color: $black-400;
    transition: color 0.2s ease;
  }

  &__input {
    height: 3.5rem;
    padding: 0 1.25rem;
    border: 2px solid $brown-400;
    border-radius: 0.75rem;
    font-size: 1.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background-color: $white;
    color: $black-400;
    width: 100%;

    &::placeholder {
      color: $brown-700;
      font-size: 1.5rem;
      opacity: 0.7;
    }

    &:focus {
      outline: none;
      border-color: $brown-200;
      box-shadow: 0 0 0 4px rgba($brown-200, 0.15);
    }

    &--error {
      border-color: #f33;

      &:focus {
        box-shadow: 0 0 0 4px rgb(255 51 51 / 15%);
      }
    }
  }

  &__button {
    height: 4.5rem;
    background: $brown-200;
    color: $white;
    border: none;
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    font-size: 1.7rem;
    font-weight: 600;
    width: 100%;

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: color.adjust($brown-200, $lightness: -5%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($brown-200, 0.3);
    }

    &:active:not(:disabled) {
      background: color.adjust($brown-200, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba($brown-200, 0.2);
    }
  }

  &__message {
    min-height: 0;
    font-size: 1.5rem;
    color: $black-400;
    line-height: 1.6;

    &--center {
      text-align: center;
    }

    &:has(.reset-password__error:not(:empty)) {
      display: block;
      min-height: 1.25rem;
    }
  }

  &__error {
    font-size: 1.5rem;
    color: #f33;
    display: block;
    font-weight: 500;
  }

  &__spinner {
    width: 2rem;
    height: 2rem;
    border: 2px solid rgb(255 255 255 / 30%);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
  }

  &__error,
  &__success {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }

  &__icon {
    margin-bottom: 0;
    background: rgba($status-green, 0.1);
    padding: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;

    &--error {
      background: rgba(#f33, 0.1);
    }

    &--error &__svg {
      color: #f33;
    }

    &--success &__svg {
      color: $status-green;
    }

    &:hover {
      transform: scale(1.05);
    }
  }

  &__svg {
    width: 4.8rem;
    height: 4.8rem;
    filter: drop-shadow(0 2px 4px rgba($status-green, 0.2));
  }

  &__subtitle {
    font-size: 2.4rem;
    font-weight: 700;
    color: $black-400;
    margin: 0;
    line-height: 1.2;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__toggle-password {
    position: absolute;
    right: 1rem;
    background: none;
    border: none;
    color: $brown-300;
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;

    &:hover {
      color: $brown-200;
    }

    i {
      font-size: 1.6rem;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
